import { Icon, Textfield, token } from "@may-i/mash-design-system";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import styled from "styled-components";

const LANGUAGES = [
  { value: "ko", label: "한국어", icon: "ic_southkorea" },
  { value: "en", label: "English", icon: "ic_unitedstates" },
] as const;

const FLAG_ICON_PROPS = { fill: "none" } as const;

export function LanguageSelector() {
  const { i18n } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [isOpen, setIsOpen] = useState(false);

  const currentIcon = i18n.language === "ko" ? "ic_southkorea" : "ic_unitedstates";

  const handleSelect = (lang: string) => {
    i18n.changeLanguage(lang);
    const next = new URLSearchParams(searchParams);
    next.set("language", lang);
    setSearchParams(next, { replace: true });
    setIsOpen(false);
  };

  return (
    <DropdownMenu.Root open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenu.Trigger asChild>
        <TriggerWrapper>
          <Textfield
            size="xSmall"
            startIcon={currentIcon}
            startIconProps={FLAG_ICON_PROPS}
            endIcon={isOpen ? "ic_chevron_up" : "ic_chevron_down"}
          />
        </TriggerWrapper>
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <StyledContent sideOffset={4} align="end">
          {LANGUAGES.map((lang) => (
            <StyledItem
              key={lang.value}
              $selected={i18n.language === lang.value}
              onClick={() => handleSelect(lang.value)}
            >
              <FlagIcon icon={lang.icon} width={18} />
              {lang.label}
            </StyledItem>
          ))}
        </StyledContent>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}

const TriggerWrapper = styled.div`
  width: fit-content;
  cursor: pointer;

  /* input을 숨기고 startIcon + endIcon만 보이게 */
  input {
    width: 0;
    min-width: 0;
    padding: 0;
  }

  /* chevron(currentColor 사용)은 GR800. 국기 SVG는 fill="none"이라 영향 없음 */
  svg[fill="currentColor"] {
    color: ${token("colors.gray.GR800")};
  }
`;

const StyledContent = styled(DropdownMenu.Content)`
  width: 200px;
  background-color: ${token("colors.gray.white")};
  border: 1px solid ${token("colors.gray.GR300")};
  border-radius: 16px;
  padding: 8px 0;
  overflow: hidden;
  box-shadow: ${token("shadows.dropdown")};
`;

const StyledItem = styled(DropdownMenu.Item)<{ $selected: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 7px 12px;
  cursor: pointer;
  font-weight: 400;
  font-size: 12px;
  line-height: 1.5;
  color: ${token("colors.text.primary")};
  background-color: ${({ $selected }) => ($selected ? "rgba(114, 142, 255, 0.08)" : "transparent")};
  outline: none;

  &:hover {
    background-color: ${token("colors.transparent.GR600.16")};
  }
`;

const FlagIcon = styled(Icon)`
  fill: none;
  flex-shrink: 0;
`;
